//src/app/routes/UpcomingDatesRoutes.tsx
import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  DashboardContainerHeader,
  ScrollContainer,
} from 'app/layout/dashboardLayout/BoxLayout';
import {
  IUpcomingDate,
  useCreateUpcomingDateMutation,
  useDeleteUpcomingDateMutation,
  useLazyGetUpcomingDatesQuery,
  useUpdateUpcomingDateMutation,
} from 'app/services/upcomingDates';
import {
  AddIcon,
  CalendarIcon,
  EditIcon,
  SaveIcon,
  TrashCanSolidIcon,
} from 'assets/icons';
import Button from 'Components/Buttons/Button';
import { USDate } from 'Components/Dates/USDate';
import { TextAreaBasic } from 'Components/Form/Basic/TextAreaBasic';
import { SingleDateInput } from 'Components/Form/Inputs/DateInput';
import Label from 'Components/Form/Label';
import { NoResultsFound } from 'Components/NoResultsFound';
import { successToast } from 'Components/Notifications';
import OverlayWindow from 'Components/OverlayWindow';
import useLoadMorePagination from 'Components/Pagination/useLoadMorePagination';
import usePagination from 'Components/Pagination/usePagination';
import { useClosePopup } from 'Components/Popups';
import { useConfirmDeleteModal } from 'Components/Popups/ConfirmDeletePopup';
import useCreateModal from 'Components/Popups/useCreateModal';

import SelectDropDown, { DDLWrap } from 'Components/SelectDropDown';
import Spinner from 'Components/Spinner';
import LoadingSpinner from 'Components/Spinner/LoadingSpinner';
import Tooltip from 'Components/Tooltips';
import { Dictionary } from 'lodash';
import moment from 'moment';
import { ReactNode, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Route } from 'react-router-dom';
import { useAuth } from 'utils/AuthProvider';
import * as Yup from 'yup';

export const validationShcmena = Yup.object({
  audience: Yup.string().nullable().required('Required'),
  description: Yup.string().trim().nullable().required('Required'),
  started_at: Yup.string().nullable().required('Required'),
  date_title: Yup.string().trim().nullable().required('Required'),
});

export function UpcomingDateForm({
  value,
  onSubmit,
}: {
  value?: Partial<IUpcomingDate>;
  onSubmit: (v: IUpcomingDate) => void;
}) {
  const {
    formState: { errors },
    handleSubmit,
    control,
    register,
    setError,
    clearErrors,
    watch,
  } = useForm<IUpcomingDate>({
    defaultValues: value,
    resolver: yupResolver(validationShcmena),
  });

  const validFrom = watch('started_at');
  const validTo = watch('ended_at');
  const dateTitle = watch('date_title');

  useEffect(() => {
    if (dateTitle && validFrom && dateTitle < validFrom) {
      setError('date_title', {
        message: 'Date must be after start date',
      });
    } else if (dateTitle && validTo && dateTitle > validTo) {
      setError('date_title', {
        message: 'Date must be before end date',
      });
    } else if (dateTitle) {
      clearErrors('date_title');
    }
  }, [validFrom, validTo, dateTitle]);
  return (
    <form
      id="upcomingDate_form"
      className="p-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Controller
        name="date_title"
        control={control}
        render={({ field }) => (
          <>
            <Label label="Date" />
            <SingleDateInput
              minDate={validFrom || moment().subtract(1, 'days').toDate()}
              maxDate={validTo ? validTo : undefined}
              onChangeStringDate={field.onChange}
              value={field.value}
              hourOffset="min"
              format="UTC"
            />
            <ErrorMessage
              errors={errors}
              name="date_title"
              render={({ message }) => (
                <p className="text-red-dark text-xs whitespace-pre-line bg-red-100 rounded-lg w-full p-1 mt-2 pl-4">
                  {message}
                </p>
              )}
            />
          </>
        )}
      />

      <TextAreaBasic
        register={register}
        name="description"
        placeholder="Description"
        label="Description"
        errors={errors}
      />
      <Controller
        name="started_at"
        control={control}
        render={({ field }) => (
          <>
            <Label label="Starts at" />
            <SingleDateInput
              minDate={moment().subtract(1, 'days').toDate()}
              maxDate={validTo ? validTo : undefined}
              onChangeStringDate={field.onChange}
              value={validFrom}
              hourOffset="min"
              format="UTC"
            />
            <ErrorMessage
              errors={errors}
              name="started_at"
              render={({ message }) => (
                <p className="text-red-dark text-xs whitespace-pre-line bg-red-100 rounded-lg w-full p-1 mt-2 pl-4">
                  {message}
                </p>
              )}
            />
          </>
        )}
      />
      <Controller
        name="ended_at"
        control={control}
        render={({ field }) => (
          <>
            <Label label="Ends at" />
            <SingleDateInput
              minDate={validFrom ? validFrom : undefined}
              onChangeStringDate={(v) =>
                v ? field.onChange(v) : field.onChange(null)
              }
              value={validTo}
              hourOffset="max"
              format="UTC"
            />
            <ErrorMessage
              errors={errors}
              name="ended_at"
              render={({ message }) => (
                <p className="text-red-dark text-xs whitespace-pre-line bg-red-100 rounded-lg w-full p-1 mt-2 capitalize pl-4">
                  {message}
                </p>
              )}
            />
          </>
        )}
      />

      <Controller
        name="audience"
        control={control}
        render={({ field }) => (
          <>
            <Label label="Audience" />

            <SelectDropDown
              onChange={field.onChange}
              value={field.value}
              options={DDLWrap(['all', 'accountants', 'clients'])}
            />

            <ErrorMessage
              errors={errors}
              name="audience"
              render={({ message }) => (
                <p className="text-red-dark text-xs whitespace-pre-line bg-red-100 rounded-lg w-full p-1 mt-2 capitalize pl-4">
                  {message}
                </p>
              )}
            />
          </>
        )}
      />
    </form>
  );
}

export function CreateUpcomingDateForm({ onCreate }: { onCreate: () => void }) {
  const [Create, { isLoading }] = useCreateUpcomingDateMutation();
  const closePopup = useClosePopup();

  const onSubmit = (form: IUpcomingDate) => {
    Create(form).unwrap().then(onCreate);
  };
  return (
    <>
      <DashboardContainerHeader title="Create New Upcoming Date"></DashboardContainerHeader>
      <UpcomingDateForm
        onSubmit={onSubmit}
        value={{ started_at: new Date().toISOString(), audience: 'all' }}
      />
      <div className="flex gap-4 justify-end p-4">
        <Button type="button" className="max-w-min" onClick={closePopup}>
          Cancel
        </Button>
        <Button
          type="submit"
          className="max-w-min"
          isLoading={isLoading}
          form="upcomingDate_form"
        >
          <SaveIcon className="w-4 h-4" /> Create
        </Button>
      </div>
    </>
  );
}

export function EditUpcomingDateForm({
  onUpdate,
  value,
}: {
  onUpdate: () => void;
  value: IUpcomingDate;
}) {
  const [Update, { isLoading }] = useUpdateUpcomingDateMutation();

  const onSubmit = (form: IUpcomingDate) => {
    Update(form).unwrap().then(onUpdate);
  };
  const closePopup = useClosePopup();

  return (
    <>
      <DashboardContainerHeader title="Edit Upcoming Date"></DashboardContainerHeader>
      <UpcomingDateForm value={value} onSubmit={onSubmit} />
      <div className="flex gap-4 justify-end p-4">
        <Button type="button" className="max-w-min" onClick={closePopup}>
          Cancel
        </Button>
        <Button
          type="submit"
          className="max-w-min"
          isLoading={isLoading}
          form="upcomingDate_form"
        >
          <SaveIcon className="w-4 h-4" /> Update
        </Button>
      </div>
    </>
  );
}

export function AdminControls({
  value,
  onEditClick,
  onDeleteClick,
}: {
  value: IUpcomingDate;
  onDeleteClick: (value: IUpcomingDate) => void;
  onEditClick: (value: IUpcomingDate) => void;
}) {
  const { isAdmin } = useAuth();
  const nowTimestamp = new Date().toISOString();
  const startedTimeStamp = new Date(value.started_at).toISOString();
  const endedTimeStamp = new Date(value.ended_at).toISOString();

  const isPending = startedTimeStamp > nowTimestamp;

  const isExpired = value.ended_at && endedTimeStamp < nowTimestamp;

  const audience =
    value.audience == 'all' ? ['Accountants', 'Clients'] : [value.audience];
  const state = isPending ? 'Pending' : isExpired ? 'Expired' : 'Active';

  return (
    <div className="flex gap-2 border-gray-100 items-center text-xs">
      <div className=" shadow  flex  gap-1 overflow-hidden rounded text-gray-700  capitalize">
        <Tooltip
          message={
            <div className=" px-1  grid grid-cols-2">
              <div className="col-span-2 text-center">{state}</div>
              <span> Starts</span> <USDate date={value.started_at} />
              {value.ended_at && (
                <>
                  <span>Ends</span> <USDate date={value.ended_at} />
                </>
              )}
            </div>
          }
        >
          <div
            className={`w-2 h-2 rounded-full cursor-pointer ${
              isPending ? 'bg-[#f6e9b7]' : isExpired ? 'bg-red-200' : 'bg-green'
            }  `}
          />
        </Tooltip>
      </div>

      {audience.map((target) => (
        <div
          key={target}
          className=" shadow px-1  rounded text-gray-700 text-sm capitalize"
        >
          {target}
        </div>
      ))}
      <Button className="ml-auto max-w-min" onClick={() => onEditClick(value)}>
        <EditIcon />
      </Button>
      <Button
        className="max-w-min"
        theme="red-outline"
        onClick={() => onDeleteClick(value)}
      >
        <TrashCanSolidIcon className="text-red-200" />
      </Button>
    </div>
  );
}

export function ViewUpcomingDates({ onLoad }: { onLoad: () => void }) {
  const { data, hasMore, loadMore, isLoading } = useLoadMorePagination(
    useLazyGetUpcomingDatesQuery,
    {
      filters: {},
      per_page: 20,
    },
    {
      skipQueryParamsCache: true,
    },
  );
  useEffect(() => {
    !isLoading && onLoad();
  }, [isLoading]);

  if (isLoading || !data) return null;

  let groupedDates: Dictionary<IUpcomingDate[]>;

  groupedDates = _.groupBy(data, 'date_title');
  let datecount = Object.entries(groupedDates).length;

  return (
    <ScrollContainer className="border border-gray-100 rounded-md shadow w-full h-full xl:rounded-tl-3xl xl:rounded-bl-3xl">
      <div className="border-gray-100 h-[55px] border-b p-2 flex gap-2 text-sm items-center text-gray-dark font-semibold">
        <CalendarIcon className="text-green w-4 h-4" /> Upcoming Events
      </div>

      <ScrollContainer>
        <div className="w-full flex flex-col">
          {data.length === 0 && (
            <NoResultsFound
              className="text-gray-light"
              Icon={<CalendarIcon className="w-10 h-10" />}
              subTitle="Upcoming calendar events will appear here."
            />
          )}
          {data.length > 0 && (
            <TimeLine groupedDates={groupedDates}>
              {(item) => (
                <div className="whitespace-pre-wrap px-4 py-2  border-gray-800 w-full ">
                  <div>{item.description}</div>
                </div>
              )}
            </TimeLine>
          )}
          <LoadingSpinner active={hasMore} loadMore={loadMore} />
        </div>
      </ScrollContainer>
    </ScrollContainer>
  );
}

function TimeLine<T>({
  groupedDates,
  children,
}: {
  groupedDates: Dictionary<T[]>;
  children: (i: T) => ReactNode;
}) {
  return (
    <div className="p-4 pr-0 flex flex-col w-full h-full">
      {Object.entries(groupedDates).map(([date, items]) => (
        <div
          className="relative  flex flex-col [&:not(:last-child)]:border-l border-gray-800 gap-2 text-sm items-start text-gray-dark "
          key={date}
        >
          <USDate className="-mt-1 text-gray-700 px-4" date={date} />

          <div className="grid pb-4 w-full">
            {items.map((item) => children(item))}
          </div>

          <div className="absolute -left-1.5 top-0 bg-white border-gray-700 border w-3 h-3 rounded-full  " />
        </div>
      ))}
    </div>
  );
}

export function UpcomingDatesList() {
  const { isAdmin } = useAuth();

  const {
    data,
    Pagination,
    refetchSamePage,
    goToFirstPage,
    refetchAfterDelete,
  } = usePagination(
    useLazyGetUpcomingDatesQuery,
    {
      filters: {},
      per_page: 10,
    },
    {
      skipQueryParamsCache: !isAdmin,
    },
  );
  const UpcomingDateFormModal = useCreateModal<IUpcomingDate>();

  const confrimDelete = useConfirmDeleteModal();
  const [Delete] = useDeleteUpcomingDateMutation();

  if (!data) return <Spinner />;

  const onCreate = () => {
    successToast('Upcoming Date Created');
    goToFirstPage();
    UpcomingDateFormModal.close();
  };

  const onUpdate = () => {
    successToast('Upcoming Date Updated');
    refetchSamePage();
    UpcomingDateFormModal.close();
  };

  const onEditClick = (data: IUpcomingDate) => {
    console.log(data);
    UpcomingDateFormModal.openWithContext({ data });
  };

  const onDeleteClick = (data: IUpcomingDate) => {
    confrimDelete('Upcoming Date', () =>
      Delete(data.id).unwrap().then(refetchAfterDelete),
    );
  };

  const groupedDates = _.groupBy(data, 'date_title');

  return (
    <ScrollContainer>
      <DashboardContainerHeader title="Upcoming Dates">
        <Button
          onClick={() => UpcomingDateFormModal.openWithContext({})}
          className="max-w-min"
        >
          <AddIcon className="w-3 h-3" />
          Add
        </Button>
      </DashboardContainerHeader>

      <ScrollContainer>
        <TimeLine groupedDates={groupedDates}>
          {(item) => (
            <div className="whitespace-pre-wrap px-4 py-2  border-gray-800 w-full ">
              <div>{item.description}</div>

              <AdminControls
                onDeleteClick={onDeleteClick}
                onEditClick={onEditClick}
                value={item}
              />
            </div>
          )}
        </TimeLine>
      </ScrollContainer>
      <Pagination />

      <UpcomingDateFormModal.Component>
        {({ data }) => (
          <OverlayWindow paddingSize="" onClose={UpcomingDateFormModal.close}>
            {!data?.id && <CreateUpcomingDateForm onCreate={onCreate} />}
            {!!data?.id && (
              <EditUpcomingDateForm value={data} onUpdate={onUpdate} />
            )}
          </OverlayWindow>
        )}
      </UpcomingDateFormModal.Component>
    </ScrollContainer>
  );
}

export default function UpcomingDatesRoutes() {
  return (
    <>
      <Route path="upcomingDates" element={<UpcomingDatesList />}></Route>
    </>
  );
}
